import { useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type NotificationLogEntry,
  type ReviewCycle,
  useReviewNotificationLog,
} from "@/hooks/use-review-cycles";
import { formatDateTime } from "@/lib/dates";

/**
 * Everything this round has emailed, and what became of it — EPIC-081 WI-306.
 *
 * Searching a person is the point: "did Sam get the reminder?" is the question
 * HR is actually asked, so an empty result for a name says so plainly rather
 * than showing a blank list.
 */

const OUTCOME: Record<NotificationLogEntry["outcome"], { label: string; tone: StatusTone }> = {
  delivered: { label: "Delivered", tone: "success" },
  sent: { label: "Sent", tone: "info" },
  queued: { label: "Waiting to send", tone: "neutral" },
  problem: { label: "Problem", tone: "danger" },
  held_back: { label: "Test mode — not sent", tone: "warning" },
};

export function RoundNotificationLog({ round }: { round: ReviewCycle }) {
  const [search, setSearch] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const debounced = useDebouncedValue(search, 300);
  const { data, isLoading, isError, error, refetch } = useReviewNotificationLog(
    round.id,
    debounced,
    round.statusKey !== "draft",
  );
  if (round.statusKey === "draft") return null;

  const all = data ?? [];
  const rows = problemsOnly ? all.filter((e) => e.outcome === "problem") : all;
  const problems = all.filter((e) => e.outcome === "problem").length;

  return (
    <DetailSection title="Notifications sent">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input
          aria-label="Search by person or email"
          placeholder="Search a person or email…"
          className="max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => setProblemsOnly(e.target.checked)}
          />
          Problems only{problems > 0 ? ` (${problems})` : ""}
        </label>
      </div>
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load the notification log"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {debounced
            ? `No email has been sent to anyone matching “${debounced}” for this round.`
            : problemsOnly
              ? "No problems — everything sent has gone through."
              : "Nothing has been sent for this round yet."}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((entry) => {
            const outcome = OUTCOME[entry.outcome];
            return (
              <li
                key={entry.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">{entry.recipientName ?? entry.recipientEmail}</p>
                  <p className="text-muted-foreground text-sm">
                    {entry.trigger} · {formatDateTime(entry.at)}
                    {entry.recipientName ? ` · ${entry.recipientEmail}` : ""}
                  </p>
                  {entry.detail && <p className="text-danger text-sm">{entry.detail}</p>}
                </div>
                <StatusPill tone={outcome.tone} label={outcome.label} />
              </li>
            );
          })}
        </ul>
      )}
    </DetailSection>
  );
}
